import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPost } from './client';

export interface MeResponse {
  id: string;
  email: string;
  displayName: string;
  role: 'admin' | 'support' | 'super_admin';
  permissions: string[];
}

export interface LoginInput {
  email: string;
  password: string;
}

/**
 * Two-stage login response from the server:
 *   - `user` populated → password OK and no MFA needed; session set.
 *   - `mfaToken` populated → password OK, but the user must complete
 *     a second-factor challenge before the session is issued. Client
 *     picks one of `factors` and posts to the matching follow-up
 *     endpoint with this token.
 */
export interface LoginResponse {
  user?: MeResponse;
  mfaToken?: string;
  factors?: Array<'totp' | 'passkey'>;
}

export interface ChangePasswordInput {
  currentPassword: string;
  newPassword: string;
}

export function useMe() {
  return useQuery({
    queryKey: ['me'],
    queryFn: () => apiGet<MeResponse>('/api/me'),
    retry: false,
    // Permissions data is security-relevant and cheap to refetch — always
    // treat it as stale on mount rather than inheriting the app-wide 30s
    // staleTime (main.tsx). This guarantees any mount of useMe() (e.g.
    // RequireSession on the page a user lands on right after login)
    // reconciles with a live GET /api/me, rather than relying on whoever
    // last wrote to this cache (a login/MFA mutation's own response) to
    // have remembered to also invalidate it.
    staleTime: 0,
  });
}

export function useHasPermission(permission: string): boolean {
  const { data } = useMe();
  return data?.permissions?.includes(permission) ?? false;
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: LoginInput) => apiPost<LoginResponse>('/api/auth/login', input),
    onSuccess: (data) => {
      // Prime the /api/me cache when login is fully complete (no MFA
      // challenge pending) so there's no loading flash on redirect — the
      // MFA-step mutations do the same on their own success. useMe()'s
      // staleTime: 0 (see session.ts's useMe definition) guarantees the next
      // mount of useMe() (e.g. RequireSession on the destination page) does a
      // real GET /api/me regardless of this snapshot, so any drift between
      // this response's permissions and the canonical endpoint's is
      // corrected immediately rather than trusted.
      if (data.user) {
        qc.setQueryData(['me'], data.user);
      }
    },
  });
}

/**
 * Submit a TOTP code against the `mfaToken` returned by `useLogin`.
 * On success the server sets the session cookie and returns the user.
 */
export function useLoginTotp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mfaToken: string; code: string }) =>
      apiPost<LoginResponse>('/api/auth/mfa/totp', input),
    onSuccess: (data) => {
      // See useLogin's onSuccess above: setQueryData avoids a loading flash,
      // and useMe()'s staleTime: 0 guarantees the next mount independently
      // reconciles with a live GET /api/me.
      if (data.user) {
        qc.setQueryData(['me'], data.user);
      }
    },
  });
}

/** Fetch the assertion challenge for passkey login. */
export function useLoginPasskeyOptions() {
  return useMutation({
    mutationFn: (input: { mfaToken: string }) =>
      apiPost<unknown>('/api/auth/mfa/passkey/options', input),
  });
}

/** Submit a verified passkey assertion to complete login. */
export function useLoginPasskeyVerify() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { mfaToken: string; response: unknown }) =>
      apiPost<LoginResponse>('/api/auth/mfa/passkey/verify', input),
    onSuccess: (data) => {
      // See useLogin's onSuccess above: setQueryData avoids a loading flash,
      // and useMe()'s staleTime: 0 guarantees the next mount independently
      // reconciles with a live GET /api/me.
      if (data.user) {
        qc.setQueryData(['me'], data.user);
      }
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<void>('/api/auth/logout'),
    onSuccess: () => {
      qc.clear();
      window.location.href = '/login';
    },
  });
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (input: ChangePasswordInput) => apiPost<void>('/api/auth/change-password', input),
  });
}
